import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Calendar, DollarSign, Users, Clock, MapPin, Settings } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Booking {
  id: string;
  client_id: string;
  start_time: string;
  duration_minutes: number;
  status: string;
  total_amount: number;
  location_address: string | null;
  client_name?: string;
}

interface TrainerMetrics {
  earnings: number;
  upcoming: number;
  total: number;
}

export default function TrainerDashboardPage() {
  const { profile } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<TrainerMetrics>({
    earnings: 0,
    upcoming: 0,
    total: 0,
  });
  const [trainerId, setTrainerId] = useState<string | null>(null);

  const loadTrainerData = useCallback(async () => {
    if (!profile) return;

    try {
      // Get trainer ID from trainers table
      const { data: trainerData, error: trainerError } = await supabase
        .from('trainers')
        .select('id')
        .eq('profile_id', profile.id)
        .single();

      if (trainerError || !trainerData) {
        console.error('Trainer profile not found:', trainerError);
        setLoading(false);
        return;
      }

      const actualTrainerId = trainerData.id;
      setTrainerId(actualTrainerId);

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

      // Fetch bookings
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('*')
        .eq('trainer_id', actualTrainerId)
        .order('start_time', { ascending: true });

      if (bookingsError) throw bookingsError;

      // Fetch client names
      const enrichedBookings = await Promise.all(
        (bookingsData || []).map(async (booking) => {
          const { data: clientProfile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', booking.client_id)
            .single();

          return {
            ...booking,
            client_name: clientProfile?.full_name || 'Client',
          };
        })
      );

      setBookings(enrichedBookings as Booking[]);

      // Calculate metrics
      const totalBookings = bookingsData?.length || 0;
      const upcomingBookings = bookingsData?.filter(
        (b) => new Date(b.start_time) > now && b.status !== 'cancelled'
      ).length || 0;

      // Calculate monthly earnings
      const monthlyBookings = bookingsData?.filter(
        (b) =>
          new Date(b.start_time) >= startOfMonth &&
          new Date(b.start_time) < endOfMonth &&
          b.status === 'completed'
      ) || [];

      const monthlyEarnings =
        monthlyBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0) || 0;

      setMetrics({
        earnings: monthlyEarnings,
        upcoming: upcomingBookings,
        total: totalBookings,
      });
    } catch (error) {
      console.error('Error loading trainer data:', error);
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    if (profile) {
      loadTrainerData();
    }
  }, [profile, loadTrainerData]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!trainerId) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-8 text-center">
              <h2 className="text-xl font-semibold text-yellow-900">
                Trainer Profile Not Found
              </h2>
              <p className="mt-2 text-sm text-yellow-700">
                Your trainer profile needs to be set up. Please contact support.
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const upcomingBookings = bookings.filter(
    (b) => new Date(b.start_time) > new Date() && b.status !== 'cancelled'
  );

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Trainer Dashboard
              </h1>
              <p className="mt-2 text-gray-600">
                Welcome back, {profile?.full_name || 'Trainer'}!
              </p>
            </div>
            <Link
              to="/profile"
              className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <Settings className="h-4 w-4" />
              Settings
            </Link>
          </div>

          {/* Stats Grid */}
          <div className="mb-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-3xl border border-blue-100 bg-white/90 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-700">Monthly Earnings</p>
              <p className="text-3xl font-bold mt-2 flex items-center gap-2">
                <DollarSign className="h-6 w-6 text-green-600" />
                ${metrics.earnings.toFixed(2)}
              </p>
            </div>
            <div className="rounded-3xl border border-blue-100 bg-white/90 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-700">Upcoming Sessions</p>
              <p className="text-3xl font-bold mt-2 flex items-center gap-2">
                <Calendar className="h-6 w-6 text-blue-600" />
                {metrics.upcoming}
              </p>
            </div>
            <div className="rounded-3xl border border-blue-100 bg-white/90 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-700">Total Bookings</p>
              <p className="text-3xl font-bold mt-2 flex items-center gap-2">
                <Users className="h-6 w-6 text-purple-600" />
                {metrics.total}
              </p>
            </div>
          </div>

          {/* Upcoming Bookings */}
          <section className="mb-8">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Upcoming Sessions</h2>
              <Link
                to="/bookings"
                className="text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                View all
              </Link>
            </div>
            {upcomingBookings.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
                <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-semibold text-gray-900">
                  No upcoming sessions
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Your upcoming bookings will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {upcomingBookings.slice(0, 5).map((booking) => (
                  <div
                    key={booking.id}
                    className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {booking.client_name}
                          </h3>
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-semibold ${
                              booking.status === 'confirmed'
                                ? 'bg-green-100 text-green-700'
                                : booking.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {booking.status}
                          </span>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {formatDate(booking.start_time)}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            {formatTime(booking.start_time)} ({booking.duration_minutes} min)
                          </div>
                          {booking.location_address && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              {booking.location_address}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="ml-4 text-right">
                        <p className="text-lg font-bold text-gray-900">
                          ${booking.total_amount?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}
